import { Libraries, useLoadScript } from '@react-google-maps/api';
import { LocationIcon } from 'assets/Icons';
import QUERY_KEYS from 'network/config/queryKeys';
import { addAddress, getAllAddress, setDefaultAddress } from 'network/services/address';
import { validateAddressDeliveryCallback } from 'network/services/order';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom'; // Add this import
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import userAddressAtom from 'store/atoms/address';
import { profileAtom } from 'store/atoms/profileAtom';
import { userLocationAtom } from 'store/atoms/userLocationAtom';
import { setCacheAddress } from 'utils/address';
import { InputField } from '../../atoms';
import { LocationIconWrapper, LocationText } from './addressDropdownStyles';

// Google Maps libraries to load
const libraries: Libraries = ['places'];

// Add custom styles for Google Places Autocomplete
const AutocompleteStyles = `
  .pac-container {
    z-index: 99999 !important;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    box-shadow: 0 8px 32px rgba(0,0,0,0.15);
    margin-top: 6px;
    font-family: inherit;
    max-width: none !important;
  }
  .pac-item {
    padding: 12px 16px;
    cursor: pointer;
    font-size: 14px;
    border-bottom: 1px solid #f0f0f0;
  }
  .pac-item:hover {
    background-color: #f8f9fa;
  }
  .pac-item-selected {
    background-color: #e3f2fd;
  }
  .pac-item:last-child {
    border-bottom: none;
  }
  .pac-matched {
    font-weight: 600;
    color: #1976d2;
  }
`;

// Add this function at the top level
const getLocalAddresses = () => {
  try {
    return JSON.parse(localStorage.getItem('localAddresses') || '[]');
  } catch (error) {
    console.error('Error reading local addresses:', error);
    return [];
  }
};

const saveLocalAddress = (address: any) => {
  try {
    const existingAddresses = getLocalAddresses();
    const updatedAddresses = [...existingAddresses, address];
    localStorage.setItem('localAddresses', JSON.stringify(updatedAddresses));
  } catch (error) {
    console.error('Error saving local address:', error);
  }
};

const setLocalAddressAsDefault = (addressToSet: any) => {
  try {
    const existingAddresses = getLocalAddresses();
    const updatedAddresses = existingAddresses.map((addr: any) => ({
      ...addr,
      status: addr.street.toLowerCase().trim() === addressToSet.street.toLowerCase().trim()
    }));
    localStorage.setItem('localAddresses', JSON.stringify(updatedAddresses));
    return addressToSet;
  } catch (error) {
    console.error('Error setting local address as default:', error);
    return null;
  }
};

// New function to sync local addresses after login
const syncLocalAddressesAfterLogin = async (
  profile: any,
  queryClient: any,
  validateAddressDeliveryCallback: any,
  addAddress: any,
  setDefaultAddress: any
) => {
  try {
    const localAddresses = getLocalAddresses();
    if (localAddresses.length === 0) return;

    // Find the default address from local storage
    const defaultLocalAddress = localAddresses.find((addr: any) => addr.status === true) || localAddresses[0];

    if (!defaultLocalAddress) return;

    const phoneNumber = profile?.phoneNumber || '+2340000000000';
    const name =
      profile?.customer_type === 'BUSINESS'
        ? profile?.business_name + ' Business'
        : (profile?.firstname || 'User') + ' ' + (profile?.lastname || 'User');

    // Validate the address
    const validationResult = await validateAddressDeliveryCallback({
      name: name,
      email: profile?.email || 'user@example.com',
      phone: phoneNumber,
      address: `${defaultLocalAddress.street}, ${defaultLocalAddress.state}, ${defaultLocalAddress.country}`
    });

    if (validationResult.status !== 'success') {
      console.warn('Address validation failed during sync');
      return;
    }

    // Add the address to the server
    const newAddressResult = await addAddress({
      status: true,
      street: defaultLocalAddress.street,
      state: defaultLocalAddress.state || 'Lagos',
      country: defaultLocalAddress.country || 'Nigeria',
      city: defaultLocalAddress.city || '',
      longitude: defaultLocalAddress.longitude,
      latitude: defaultLocalAddress.latitude,
      address_code: validationResult?.data?.address_code || 0,
      phoneNumber: validationResult?.data?.phone || phoneNumber
    });

    // Clear local addresses after successful sync
    localStorage.removeItem('localAddresses');

    // Invalidate queries to refresh the UI
    queryClient.invalidateQueries(QUERY_KEYS.CUSTOMER_ADDRESSES);

    enqueueSnackbar('Your saved address has been synced to your account', { variant: 'success' });

    return newAddressResult;
  } catch (error) {
    console.error('Error syncing local addresses after login:', error);
  }
};

export const AddressInput = () => {
  const navigate = useNavigate(); // Add this hook
  const [searchInput, setSearchInput] = useState('');
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [isSavingAddress, setIsSavingAddress] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<google.maps.places.PlaceResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const [hasAddress, setHasAddress] = useState(false);
  const [hasSyncedAfterLogin, setHasSyncedAfterLogin] = useState(false); // Add this state

  const { data: addresses, refetch: refetchAddresses } = useQuery({
    queryKey: QUERY_KEYS.CUSTOMER_ADDRESSES,
    queryFn: getAllAddress,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  });

  const [userAddress, setUserAddress] = useRecoilState(userAddressAtom);
  const setUserLocation = useSetRecoilState(userLocationAtom);
  const profile = useRecoilValue(profileAtom);
  const locationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  // Load Google Maps API
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY || '',
    libraries
  });

  // Show error message if the API fails to load
  useEffect(() => {
    if (loadError) {
      console.error('Google Maps API failed to load:', loadError);
    }
  }, [loadError]);

  // Sync local addresses after login
  useEffect(() => {
    if (profile && !hasSyncedAfterLogin) {
      const localAddresses = getLocalAddresses();
      if (localAddresses.length > 0) {
        syncLocalAddressesAfterLogin(
          profile,
          queryClient,
          validateAddressDeliveryCallback,
          addAddress,
          setDefaultAddress
        );
        setHasSyncedAfterLogin(true);
      }
    }
  }, [profile, hasSyncedAfterLogin, queryClient]);

  // Set initial placeholder from local storage if user is not logged in
  useEffect(() => {
    if (!profile && !searchInput) {
      const localAddresses = JSON.parse(localStorage.getItem('localAddresses') || '[]');
      if (localAddresses.length > 0) {
        const defaultAddress = localAddresses.find((addr: any) => addr.status === true) || localAddresses[0];
        if (defaultAddress && searchInputRef.current) {
          searchInputRef.current.placeholder = defaultAddress.street;
          setUserAddress(defaultAddress.street);
          setCacheAddress(defaultAddress.street);
        }
      }
    }
  }, [profile, searchInput]);

  // Modify the useEffect that sets default address
  useEffect(() => {
    const addressData = addresses?.data;
    if (addressData && addressData.length > 0) {
      const defaultAddress = addressData.find(addr => addr.status === true) || addressData[0];
      if (defaultAddress) {
        setSearchInput(defaultAddress.street);
        if (inputRef.current) {
          inputRef.current.placeholder = defaultAddress.street;
          setHasAddress(true);
        }
      }
    } else if (!profile) {
      // If user is not logged in, try to load from local storage
      const localAddresses = getLocalAddresses();
      if (localAddresses.length > 0) {
        const defaultAddress = localAddresses.find((addr: any) => addr.status === true) || localAddresses[0];
        if (defaultAddress) {
          setSearchInput(defaultAddress.street);
          if (inputRef.current) {
            inputRef.current.placeholder = defaultAddress.street;
            setHasAddress(true);
          }
          setUserAddress(defaultAddress.street);
          setCacheAddress(defaultAddress.street);
        }
      }
    }
  }, [addresses?.data, profile, setUserAddress]);

  // Initialize Google Places Autocomplete
  useEffect(() => {
    // Add custom styles for Google Places Autocomplete
    const styleEl = document.createElement('style');
    styleEl.type = 'text/css';
    styleEl.appendChild(document.createTextNode(AutocompleteStyles));
    document.head.appendChild(styleEl);

    if (isLoaded && searchInputRef.current && !autocompleteRef.current) {
      try {
        autocompleteRef.current = new window.google.maps.places.Autocomplete(searchInputRef.current, {
          types: ['address'],
          componentRestrictions: { country: 'ng' }, // Restrict to Nigeria
          fields: ['address_components', 'formatted_address', 'geometry', 'name']
        });

        // Listen for place selection
        autocompleteRef.current?.addListener('place_changed', async () => {
          const place = autocompleteRef.current?.getPlace();

          if (place && place.geometry && place.geometry.location) {
            const formattedAddress = place.formatted_address || '';

            // Extract address components
            let state = '';
            let city = '';
            let country = '';
            let zipCode = '';

            if (place.address_components) {
              place.address_components.forEach((component: any) => {
                if (component.types.includes('administrative_area_level_1')) {
                  state = component.long_name;
                } else if (component.types.includes('locality')) {
                  city = component.long_name;
                } else if (component.types.includes('country')) {
                  country = component.long_name;
                } else if (component.types.includes('postal_code')) {
                  zipCode = component.long_name;
                }
              });
            }

            // Get coordinates
            const latitude = place.geometry.location.lat();
            const longitude = place.geometry.location.lng();

            // Clear search input
            setSearchInput('');

            // Update location state
            setUserLocation({
              lat: latitude.toString(),
              display_name: formattedAddress,
              name: formattedAddress,
              place_id: place.place_id || '0',
              lon: longitude.toString(),
              address: {
                state: state || 'Lagos',
                city: city || '',
                country: country || 'Nigeria',
                postcode: zipCode || ''
              }
            });

            // Save the address automatically
            try {
              await saveLocationAsAddress(
                formattedAddress,
                state || 'Lagos',
                city || '',
                country || 'Nigeria',
                zipCode || '',
                latitude,
                longitude
              );

              // Navigate to supermarket categories after successful address selection
              navigate('/categories/supermarket?category=3');
            } catch (error) {
              console.error('Error saving autocomplete address:', error);
            }
          }
        });
      } catch (error) {
        console.error('Error initializing Google Places Autocomplete:', error);
      }
    }

    return () => {
      // Clean up styles when component unmounts
      if (styleEl && styleEl.parentNode) {
        styleEl.parentNode.removeChild(styleEl);
      }
    };
  }, [isLoaded, navigate]); // Add navigate to dependencies

  const updateUIWithAddress = useCallback(
    (address: string) => {
      setSearchInput(address);
      if (inputRef.current) {
        inputRef.current.placeholder = address;
        setHasAddress(true);
      }
      setUserAddress(address);
      setCacheAddress(address);
    },
    [setUserAddress]
  );

  const saveLocationAsAddress = useCallback(
    async (
      formattedAddress: string,
      state: string,
      city: string,
      country: string,
      zipCode: string,
      latitude: number,
      longitude: number
    ) => {
      try {
        setIsSavingAddress(true);

        // If user is not logged in, save to local storage
        if (!profile) {
          const localAddress = {
            street: formattedAddress,
            state: state || 'Lagos',
            country: country || 'Nigeria',
            city: city || '',
            longitude: longitude,
            latitude: latitude,
            address_code: 0,
            phoneNumber: '+2340000000000',
            status: true
          };

          // Get existing addresses from local storage
          const existingAddresses = getLocalAddresses();

          // Check if address already exists
          const existingAddress = existingAddresses.find(
            (addr: any) => addr.street.toLowerCase().trim() === formattedAddress.toLowerCase().trim()
          );

          if (existingAddress) {
            // Set existing address as default and update UI
            const updatedAddress = setLocalAddressAsDefault(existingAddress);
            if (updatedAddress) {
              updateUIWithAddress(updatedAddress.street);
              enqueueSnackbar('Address already exists and has been set as your default address', {
                variant: 'success'
              });
              return updatedAddress;
            }
          } else {
            // Add new address to local storage
            saveLocalAddress(localAddress);
            updateUIWithAddress(formattedAddress);
            enqueueSnackbar('Address saved successfully', { variant: 'success' });
            return localAddress;
          }
        }

        // For logged-in users, check if address already exists
        const existingAddress = addresses?.data?.find(
          address => address.street.toLowerCase().trim() === formattedAddress.toLowerCase().trim()
        );

        if (existingAddress) {
          // Set existing address as default
          await setDefaultAddress(existingAddress.id.toString());
          queryClient.invalidateQueries(QUERY_KEYS.CUSTOMER_ADDRESSES);
          updateUIWithAddress(existingAddress.street);
          enqueueSnackbar('Address already exists and has been set as your default address', { variant: 'success' });
          return existingAddress;
        }

        // Format the phone number from profile or use a placeholder
        let phoneNumber = profile?.phoneNumber || '';
        if (!phoneNumber) {
          phoneNumber = '+2340000000000';
        }

        const name =
          profile?.customer_type === 'BUSINESS'
            ? profile?.business_name + ' Business'
            : (profile?.firstname || 'User') + ' ' + (profile?.lastname || 'User');

        const validationResult = await validateAddressDeliveryCallback({
          name: name,
          email: profile?.email || 'user@example.com',
          phone: phoneNumber,
          address: `${formattedAddress}, ${state}, ${country}`
        });

        if (validationResult.status !== 'success') {
          throw new Error('Address validation failed');
        }

        const newAddressResult = await addAddress({
          status: true,
          street: formattedAddress,
          state: state || 'Lagos',
          country: country || 'Nigeria',
          city: city || '',
          longitude: longitude,
          latitude: latitude,
          address_code: validationResult?.data?.address_code || 0,
          phoneNumber: validationResult?.data?.phone || phoneNumber
        });

        queryClient.invalidateQueries(QUERY_KEYS.CUSTOMER_ADDRESSES);
        updateUIWithAddress(formattedAddress);
        enqueueSnackbar('Address saved successfully', { variant: 'success' });

        return newAddressResult;
      } catch (error) {
        console.error('Error saving address:', error);
        enqueueSnackbar('Failed to save address. Please try again.', { variant: 'error' });
        throw error;
      } finally {
        setIsSavingAddress(false);
      }
    },
    [profile, validateAddressDeliveryCallback, addAddress, queryClient, addresses?.data, updateUIWithAddress]
  );

  const getCurrentLocation = useCallback(() => {
    if (isLoadingLocation) return;

    if (locationTimeoutRef.current) {
      clearTimeout(locationTimeoutRef.current);
    }

    if (!navigator.geolocation) {
      enqueueSnackbar('Geolocation is not supported by your browser', { variant: 'error' });
      return;
    }

    setIsLoadingLocation(true);

    navigator.geolocation.getCurrentPosition(
      async position => {
        try {
          const { latitude, longitude } = position.coords;

          // Use Google Maps Geocoding API to get address from coordinates
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${process.env.REACT_APP_GOOGLE_API_KEY}`
          );

          const data = await response.json();

          if (data.status === 'OK' && data.results.length > 0) {
            const addressResult = data.results[0];
            const formattedAddress = addressResult.formatted_address;

            // Extract components
            let state = '';
            let city = '';
            let country = '';
            let zipCode = '';

            addressResult.address_components.forEach((component: any) => {
              if (component.types.includes('administrative_area_level_1')) {
                state = component.long_name;
              } else if (component.types.includes('locality')) {
                city = component.long_name;
              } else if (component.types.includes('country')) {
                country = component.long_name;
              } else if (component.types.includes('postal_code')) {
                zipCode = component.long_name;
              }
            });

            setUserLocation({
              lat: latitude.toString(),
              display_name: formattedAddress,
              name: formattedAddress,
              place_id: '0',
              lon: longitude.toString(),
              address: {
                state: state,
                city: city,
                country: country,
                postcode: zipCode
              }
            });

            // Automatically save the address and make it default
            try {
              await saveLocationAsAddress(formattedAddress, state, city, country, zipCode, latitude, longitude);

              // Navigate to supermarket categories after successful location detection
              navigate('/categories/supermarket?category=3');
            } catch (saveError) {
              console.error('Error during address save:', saveError);
            }
          } else {
            throw new Error(data.status || 'Could not retrieve address from coordinates');
          }
        } catch (error) {
          console.error('Error getting location:', error);
          enqueueSnackbar(`Failed to get your current location: ${(error as any).message || 'Unknown error'}`, {
            variant: 'error'
          });
        } finally {
          locationTimeoutRef.current = setTimeout(() => {
            setIsLoadingLocation(false);
          }, 1000);
        }
      },
      error => {
        console.error('Geolocation error:', error);
        let errorMessage = 'Could not get your current location';

        if (error.code === 1) {
          errorMessage = 'Location access denied. Please enable location services in your browser.';
        } else if (error.code === 2) {
          errorMessage = 'Location unavailable. Please try again.';
        } else if (error.code === 3) {
          errorMessage = 'Location request timed out. Please try again.';
        }

        enqueueSnackbar(errorMessage, { variant: 'error' });

        locationTimeoutRef.current = setTimeout(() => {
          setIsLoadingLocation(false);
        }, 1000);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, [isLoadingLocation, setUserLocation, saveLocationAsAddress, navigate]); // Add navigate to dependencies

  const handleSaveAddress = async () => {
    if (!selectedAddress) return;

    setIsSavingAddress(true);
    setError(null);

    try {
      const addressComponents = selectedAddress.address_components || [];
      const streetNumber =
        addressComponents.find(component => component.types.includes('street_number'))?.long_name || '';
      const route = addressComponents.find(component => component.types.includes('route'))?.long_name || '';
      const city = addressComponents.find(component => component.types.includes('locality'))?.long_name || '';
      const state =
        addressComponents.find(component => component.types.includes('administrative_area_level_1'))?.long_name || '';
      const country = addressComponents.find(component => component.types.includes('country'))?.long_name || '';
      const postalCode = addressComponents.find(component => component.types.includes('postal_code'))?.long_name || '';

      const addressData = {
        street: `${streetNumber} ${route}`.trim(),
        city,
        state,
        country,
        postalCode,
        latitude: selectedAddress.geometry?.location?.lat() || 0,
        longitude: selectedAddress.geometry?.location?.lng() || 0,
        isDefault: addresses?.data?.length === 0, // Set as default if it's the first address
        status: true, // Set as active
        address_code: parseInt(postalCode.replace(/\D/g, '') || '0', 10), // Convert postal code to number
        phoneNumber: 0 // Default to 0 since we don't have phone number input yet
      };

      await addAddress(addressData);
      enqueueSnackbar('Address saved successfully', { variant: 'success' });
      setSearchInput('');
      setSelectedAddress(null);
      refetchAddresses(); // Manually refetch addresses after adding a new one

      // Navigate to supermarket categories after successful address save
      navigate('/categories/supermarket?category=3');
    } catch (err) {
      setError('Failed to save address. Please try again.');
      enqueueSnackbar('Failed to save address', { variant: 'error' });
    } finally {
      setIsSavingAddress(false);
    }
  };

  return (
    <div className="relative">
      <div className="flex flex-col sm:flex-row items-center gap-3">
        <div className="w-full sm:flex-1 relative">
          <InputField
            type="text"
            name="search"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            disabled={isLoading || isSavingAddress}
            placeholder={
              isLoaded
                ? searchInput || (profile ? 'Enter your delivery address...' : 'Enter your delivery address...')
                : 'Loading address search...'
            }
            inputRef={searchInputRef}
            autoComplete="off"
            className="relative bg-white/90 backdrop-blur-sm border border-white/20 rounded-xl shadow-lg"
            onFocus={() => {
              if (autocompleteRef.current) {
                autocompleteRef.current.set('place', null);
              }
            }}
          />
        </div>
        <div className="relative flex items-center gap-2">
          {hasAddress && (
            <div className="text-green-500">
              <LocationIcon className="w-6 h-6" />
            </div>
          )}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-[#0066FF] to-[#0052CC] rounded-lg blur-sm"></div>
            <button
              onClick={getCurrentLocation}
              disabled={isLoadingLocation || isSavingAddress || hasAddress}
              className="relative flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#0066FF] to-[#0052CC] text-white rounded-lg hover:from-[#0052CC] hover:to-[#0047B3] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg">
              <LocationIconWrapper $isLoading={isLoadingLocation || isSavingAddress}>
                <LocationIcon />
              </LocationIconWrapper>
              <LocationText>
                {isLoadingLocation ? (
                  <>
                    Locating
                    <span className="loading-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </span>
                  </>
                ) : isSavingAddress ? (
                  <>
                    Saving
                    <span className="loading-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </span>
                  </>
                ) : (
                  'Use My Location'
                )}
              </LocationText>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
